import React, { useMemo, useState } from "react";
import HelpLink from "../help/HelpLink";
import { cn } from "../../lib/utils";
import { DURATIONS, durationLabel, machinesWithPower, powerCurve } from "../../utils/progress";

const W = 300;
const H = 120;
const L = 30; // room for the watts scale
const B = 14; // room for the duration labels
const TICKS = [
  [5, "5s"],
  [60, "1m"],
  [300, "5m"],
  [1200, "20m"],
  [3600, "60m"],
];
const LISTED = [5, 60, 300, 1200, 3600];

/**
 * Best average power for 5 s to 60 min (#203): the last 8 weeks against all
 * time, on the bike and rower separately. A best set in the last two weeks is
 * marked. Durations run on a log scale, the usual way to draw this curve, so
 * sprints and long efforts both get room.
 */
const PowerCurveCard = ({ rows }) => {
  const machines = useMemo(() => machinesWithPower(rows), [rows]);
  const [picked, setPicked] = useState(null);
  const dev = machines.some(([d]) => d === picked) ? picked : machines[0]?.[0];
  const curve = useMemo(() => (dev ? powerCurve(rows, dev) : null), [rows, dev]);
  if (!curve) return null;

  const present = DURATIONS.filter((d) => curve.allTime[d]);
  const top = Math.ceil((Math.max(...present.map((d) => curve.allTime[d].watts)) * 1.08) / 100) * 100;
  const x = (s) => L + (Math.log(s / 5) / Math.log(3600 / 5)) * (W - L - 14);
  const y = (v) => 4 + (H - B - 4) * (1 - v / top);
  const path = (series) =>
    DURATIONS.filter((d) => series[d])
      .map((d, i) => `${i ? "L" : "M"}${x(d).toFixed(1)},${y(series[d].watts).toFixed(1)}`)
      .join("");
  const step = top > 600 ? 200 : 100;
  const lines = [];
  for (let v = step; v < top; v += step) lines.push(v);
  const freshest = curve.newBests[curve.newBests.length - 1];

  return (
    <section aria-label="Power curve" className="grid gap-3 rounded-2xl border border-border bg-card p-3.5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1 text-[15px] font-bold text-white">
          Power curve
          <HelpLink topic="progress#power-curve" label="About the power curve" />
        </h3>
        {machines.length > 1 && (
          <div
            role="radiogroup"
            aria-label="Machine"
            className="inline-flex overflow-hidden rounded-lg border border-border text-[11px]"
          >
            {machines.map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={dev === id}
                onClick={() => setPicked(id)}
                className={cn(
                  "min-h-[28px] px-2.5",
                  dev === id ? "bg-white/10 text-foreground" : "text-muted-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="block w-full" role="img" aria-label="Best power by duration">
        {lines.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - 14} y1={y(v)} y2={y(v)} stroke="rgba(255,255,255,.06)" />
            <text x={L - 4} y={y(v) + 3} textAnchor="end" fontSize="9" className="fill-muted-foreground">
              {v}
            </text>
          </g>
        ))}
        {TICKS.map(([s, label]) => (
          <text key={s} x={x(s)} y={H - 2} textAnchor="middle" fontSize="9" className="fill-muted-foreground">
            {label}
          </text>
        ))}
        <path
          d={path(curve.allTime)}
          fill="none"
          stroke="hsl(var(--foreground))"
          strokeOpacity=".45"
          strokeWidth="1.5"
          strokeDasharray="3 3"
        />
        <path d={path(curve.recent)} fill="none" stroke="hsl(var(--foreground))" strokeWidth="2" />
        {freshest && (
          <circle cx={x(freshest)} cy={y(curve.allTime[freshest].watts)} r="3.5" fill="hsl(var(--primary))" />
        )}
      </svg>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground" aria-hidden="true">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-[2px] w-3 rounded-full bg-foreground" />
          Last 8 weeks
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-[2px] w-3 rounded-full bg-foreground/45" />
          All time
        </span>
        {freshest && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-primary" />
            New best
          </span>
        )}
      </div>
      <dl className="grid gap-1 text-xs tabular-nums">
        {LISTED.filter((d) => curve.allTime[d]).map((d) => (
          <div key={d} className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Best {durationLabel(d)}</dt>
            <dd className="font-semibold text-foreground">
              {curve.allTime[d].watts} W
              {curve.newBests.includes(d) && <span className="ml-1 font-normal text-primary">new</span>}
              {curve.recent[d] && curve.recent[d].watts !== curve.allTime[d].watts && (
                <span className="ml-1 font-normal text-muted-foreground">· {curve.recent[d].watts} W lately</span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default PowerCurveCard;
